import React, { useCallback, useEffect, useMemo, useState, lazy, Suspense } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useAuth, AuthProvider } from '../contexts/AuthContext';
import { WindowsProvider, useWindows, FOCUS_WINDOW_ID } from '../contexts/WindowsContext';
import { useFocusSession } from '../hooks/useFocusSession';
import { useTodos } from '../hooks/useTodos';
import { useTeams } from '../hooks/useTeams';
import { useNotification } from '../hooks/useNotification';
import { Notification } from '../components/Notification';
import AuthForm from '../components/AuthForm';
import { ViewTabs } from '../components/ViewTabs';
import { SearchFilterBar } from '../components/SearchFilterBar';
import ErrorBoundary from '../components/ErrorBoundary';
import { WindowTabs, WindowContainer } from '../components/windows';
import { TaskStatus, RecurrenceInterval } from '../types';
import type { Todo, TodoCreate, TeamWithMembers, Event } from '../types';

// App components and hooks
import { useAppState } from './hooks/useAppState';
import { useTodoHandlers } from './hooks/useTodoHandlers';
import { filterTodos } from './utils/filterTodos';
import { AppHeader } from './components/AppHeader';
import { AppViews } from './components/AppViews';
import { AppModals } from './components/AppModals';
import { AIChatModal } from '../components/ai/AIChatModal';
import { LocalAgentPairing } from '../components/ai/LocalAgentConnection';
import { ProcessInboxModal } from '../components/ProcessInboxModal';
import { UserSettingsModal } from '../components/UserSettings';
import { getInboxTasks } from '../utils/inboxFilter';
import { GoogleCalendarReauthBanner } from '../components/GoogleCalendarReauthBanner';

// Lazy-loaded mobile shell (desktop users never download this code)
const MobileShell = lazy(() => import('../components/mobile/MobileShell').then(m => ({ default: m.MobileShell })));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchInterval: false,
    },
  },
});

function TodoApp() {
  const { isAuthenticated, isLoading: isAuthLoading, user } = useAuth();

  // Track initial auth check separately so login/register loading doesn't unmount AuthForm
  const [authInitialized, setAuthInitialized] = useState(false);
  useEffect(() => {
    if (!isAuthLoading && !authInitialized) setAuthInitialized(true);
  }, [isAuthLoading, authInitialized]);

  const { notification, showNotification, hideNotification } = useNotification();
  const { isMobile, setActiveWindow } = useWindows();
  const { session: focusSession } = useFocusSession();
  const focusStatus = focusSession?.status;
  const focusLocked = focusStatus === 'active' || focusStatus === 'paused';

  // When a focus session is active/paused, auto-switch to Focus tab
  useEffect(() => {
    if (focusStatus && ['planning', 'active', 'paused'].includes(focusStatus)) {
      setActiveWindow(FOCUS_WINDOW_ID);
    }
  }, [focusStatus, setActiveWindow]);

  // Dynamic theme-color meta tag for mobile browser chrome
  useEffect(() => {
    const isDark = document.body.classList.contains('dark');
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute('content', isDark ? '#111827' : '#ffffff');
  });

  // View mode — toggle only available when classicViewEnabled is true in settings
  const classicViewUnlocked = user?.preferences?.classicViewEnabled === true;
  const [viewMode, setViewMode] = useState<'classic' | 'windows'>('windows');
  // Force windows mode when classic view is disabled in settings
  useEffect(() => {
    if (!classicViewUnlocked) setViewMode('windows');
  }, [classicViewUnlocked]);

  // App state
  const appState = useAppState();
  const {
    activeView, setActiveView,
    selectedTodo, setSelectedTodo,
    showTodoForm, setShowTodoForm,
    todoFormRef, modalStack, setModalStack,
    addingChildToParentId, setAddingChildToParentId,
    initialDueDate, setInitialDueDate,
    initialTodoDate, setInitialTodoDate,
    initialActiveInWeek, setInitialActiveInWeek,
    initialActiveInMonth, setInitialActiveInMonth,
    showFilters, setShowFilters,
    searchQuery, setSearchQuery,
    showPomodoroTimer, setShowPomodoroTimer,
    currentPomodoroTask, setCurrentPomodoroTask,
    showAIAssistant, setShowAIAssistant,
    selectedTeamId, setSelectedTeamId,
    showTeamModal, setShowTeamModal,
    showJoinTeamModal, setShowJoinTeamModal,
    editingTeam, setEditingTeam,
    currentFilters
  } = appState;

  const [showMobileSettings, setShowMobileSettings] = useState(false);

  // Data: Teams
  const { teams } = useTeams();
  const currentTeam = teams.find(team => team.id === selectedTeamId);
  const isPersonalMode = selectedTeamId === null;
  const teamMembers = currentTeam ? currentTeam.members.map(member => ({
    id: member.user.id, name: member.user.name, email: member.user.email
  })) : [];
  const currentUserMembership = currentTeam && user
    ? currentTeam.members.find(member => member.user_id === user.id)
    : null;
  const shouldIncludePersonalTasks = isPersonalMode
    ? true
    : (currentUserMembership ? currentUserMembership.show_personal_tasks : false);

  // Data: Todos
  const {
    todos: allTodos, allTags, isLoading: isLoadingTodos,
    createTodo, updateTodo, deleteTodo,
    isCreating: isCreatingTodo, isUpdating: isUpdatingTodo
  } = useTodos({
    team_id: selectedTeamId,
    include_personal: shouldIncludePersonalTasks,
    parent_only: false, child_only: false
  });

  const todos = filterTodos(allTodos, {
    searchQuery,
    statusFilter: currentFilters.statusFilter,
    horizonFilter: currentFilters.horizonFilter,
    tagsFilter: currentFilters.tagsFilter,
    taskTypeFilter: currentFilters.taskTypeFilter
  });

  // Todo CRUD handlers (extracted hook — eliminates ~200 lines of duplication)
  const {
    handleUpdateTodo, handleAutoSaveTodo, handleCreateTodo,
    handleDeleteTodo, handleStatusChange, handleCloneTodo,
    handleTodoDateChange, handleMakeChild,
  } = useTodoHandlers({
    allTodos, selectedTodo, updateTodo, createTodo, deleteTodo,
    showNotification, setSelectedTodo,
  });

  // Wrap handleCreateTodo to also reset form state
  const handleCreateTodoAndClose = useCallback((todoData: TodoCreate) => {
    handleCreateTodo(todoData);
    setShowTodoForm(false);
    setAddingChildToParentId(null);
    setInitialDueDate(null);
    setInitialTodoDate(null);
    setInitialActiveInWeek(false);
    setInitialActiveInMonth(false);
  }, [handleCreateTodo, setShowTodoForm, setAddingChildToParentId, setInitialDueDate, setInitialTodoDate, setInitialActiveInWeek, setInitialActiveInMonth]);

  // Create a task immediately via API then open it in the edit modal
  const handleQuickCreateAndOpen = useCallback((options?: {
    deadline?: string | null;
    parentId?: string | null;
    teamId?: string | null;
  }) => {
    const todoData: TodoCreate = {
      title: '',
      status: TaskStatus.NOT_STARTED,
      horizon: null,
      recurrence_interval: RecurrenceInterval.NEVER,
      tags: [],
      deadline: options?.deadline || null,
      parent_id: options?.parentId || null,
      team_id: options?.teamId || undefined,
      creator_id: user?.id,
    };
    createTodo(todoData, {
      onSuccess: (newTodo: Todo) => {
        setSelectedTodo(newTodo);
      },
      onError: (error: unknown) => {
        console.error('Failed to create task:', error);
        showNotification('error', 'Failed to create task. Please try again.');
      }
    });
  }, [createTodo, setSelectedTodo, showNotification, user?.id]);

  // Process Inbox modal (must be before modal return useEffect)
  const [showProcessInbox, setShowProcessInbox] = useState(false);
  const inboxCount = useMemo(() => getInboxTasks(allTodos).length, [allTodos]);

  // Modal return flow
  useEffect(() => {
    if (!selectedTodo && !showTodoForm && modalStack.previousModal) {
      const timer = setTimeout(() => {
        if (modalStack.previousModal === 'process-inbox') {
          setShowProcessInbox(true);
        }
        setModalStack({});
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [selectedTodo, showTodoForm, modalStack, setModalStack, setShowProcessInbox]);

  // Widget task click handler
  useEffect(() => {
    const handleOpenTaskFromWidget = (e: CustomEvent<{ todo: Todo }>) => {
      setSelectedTodo(e.detail.todo);
    };
    window.addEventListener('openTaskFromWidget', handleOpenTaskFromWidget as EventListener);
    return () => window.removeEventListener('openTaskFromWidget', handleOpenTaskFromWidget as EventListener);
  }, [setSelectedTodo]);

  // Schedule todo as event handler
  const [scheduleTodo, setScheduleTodo] = useState<Todo | null>(null);
  useEffect(() => {
    const handleScheduleTodo = (e: CustomEvent<{ todo: Todo }>) => {
      setScheduleTodo(e.detail.todo);
    };
    window.addEventListener('scheduleTodoEvent', handleScheduleTodo as EventListener);
    return () => window.removeEventListener('scheduleTodoEvent', handleScheduleTodo as EventListener);
  }, []);

  // Open standalone event from widget
  const [editEvent, setEditEvent] = useState<Event | null>(null);
  useEffect(() => {
    const handleOpenEvent = (e: CustomEvent<{ event: Event }>) => {
      setEditEvent(e.detail.event);
    };
    window.addEventListener('openEventFromWidget', handleOpenEvent as EventListener);
    return () => window.removeEventListener('openEventFromWidget', handleOpenEvent as EventListener);
  }, []);

  // Open process inbox from widgets/reviews
  useEffect(() => {
    const handleOpenProcessInbox = () => setShowProcessInbox(true);
    window.addEventListener('openProcessInbox', handleOpenProcessInbox);
    return () => window.removeEventListener('openProcessInbox', handleOpenProcessInbox);
  }, []);

  // Open AI conversation from widget (e.g. "via AI" badge in activity feed)
  const [aiConversationId, setAIConversationId] = useState<string | null>(null);
  useEffect(() => {
    const handleOpenAIConversation = (e: CustomEvent<{ conversationId: string }>) => {
      setAIConversationId(e.detail.conversationId);
    };
    window.addEventListener('openAIConversation', handleOpenAIConversation as EventListener);
    return () => window.removeEventListener('openAIConversation', handleOpenAIConversation as EventListener);
  }, []);

  // Navigate to review from widgets
  useEffect(() => {
    const handleOpenWeeklyReview = () => {
      sessionStorage.setItem('pendingReview', 'weekly');
      setActiveView('week');
    };
    const handleOpenMonthlyReview = () => {
      sessionStorage.setItem('pendingReview', 'monthly');
      setActiveView('month');
    };
    const handleOpenYearlyReview = () => {
      sessionStorage.setItem('pendingReview', 'yearly');
      setActiveView('year');
    };
    window.addEventListener('openWeeklyReviewFromWidget', handleOpenWeeklyReview as EventListener);
    window.addEventListener('openMonthlyReviewFromWidget', handleOpenMonthlyReview as EventListener);
    window.addEventListener('openYearlyReviewFromWidget', handleOpenYearlyReview as EventListener);
    return () => {
      window.removeEventListener('openWeeklyReviewFromWidget', handleOpenWeeklyReview as EventListener);
      window.removeEventListener('openMonthlyReviewFromWidget', handleOpenMonthlyReview as EventListener);
      window.removeEventListener('openYearlyReviewFromWidget', handleOpenYearlyReview as EventListener);
    };
  }, [setActiveView]);

  // Modal context handlers — auto-save on close, delete empty drafts
  const handleCloseTodoModal = useCallback(() => {
    const formHasTitle = todoFormRef.current?.hasTitle?.() ?? !!selectedTodo?.title.trim();
    if (selectedTodo && !formHasTitle) {
      // No title in form — delete the draft task
      deleteTodo(selectedTodo.id);
    } else {
      todoFormRef.current?.autoSave();
    }
    setSelectedTodo(null);
  }, [todoFormRef, selectedTodo, deleteTodo, setSelectedTodo]);
  const handleCancelTodoModal = useCallback(() => {
    const formHasTitle = todoFormRef.current?.hasTitle?.() ?? !!selectedTodo?.title.trim();
    if (selectedTodo && !formHasTitle) {
      // No title in form — delete the draft task
      deleteTodo(selectedTodo.id);
    } else {
      todoFormRef.current?.autoSave();
    }
    setSelectedTodo(null);
    setShowTodoForm(false);
  }, [todoFormRef, selectedTodo, deleteTodo, setSelectedTodo, setShowTodoForm]);

  const handleTodoClickWithContext = useCallback((todo: Todo, modalContext?: {
    previousModal: string; previousPhase?: string; previousState?: Record<string, unknown>;
  }) => {
    if (modalContext) setModalStack(modalContext);
    setSelectedTodo(todo);
  }, [setModalStack, setSelectedTodo]);

  const handleSubtaskClick = useCallback((subtask: Todo) => {
    setModalStack({ previousModal: 'todo-detail', previousState: { selectedTodoId: selectedTodo?.id } });
    setSelectedTodo(subtask);
  }, [selectedTodo, setModalStack, setSelectedTodo]);

  const handleParentClick = useCallback((parentTask: Todo) => {
    setModalStack({ previousModal: 'todo-detail', previousState: { selectedTodoId: selectedTodo?.id } });
    setSelectedTodo(parentTask);
  }, [selectedTodo, setModalStack, setSelectedTodo]);

  const handleAddChild = useCallback((parentId: string) => {
    handleQuickCreateAndOpen({ parentId });
  }, [handleQuickCreateAndOpen]);

  // Team handlers
  const handleTeamSelect = useCallback((teamId: string | null) => setSelectedTeamId(teamId), [setSelectedTeamId]);
  const handleCreateTeam = useCallback(() => { setEditingTeam(null); setShowTeamModal(true); }, [setEditingTeam, setShowTeamModal]);
  const handleJoinTeam = useCallback(() => setShowJoinTeamModal(true), [setShowJoinTeamModal]);
  const handleTeamSettings = useCallback((team: TeamWithMembers) => { setEditingTeam(team); setShowTeamModal(true); }, [setEditingTeam, setShowTeamModal]);

  const handleStartPomodoro = useCallback((todo: Todo) => {
    setCurrentPomodoroTask(todo);
    setShowPomodoroTimer(true);
  }, [setCurrentPomodoroTask, setShowPomodoroTimer]);

  const handleCalendarCreateTodo = useCallback((dueDate: string) => {
    handleQuickCreateAndOpen({ deadline: dueDate });
  }, [handleQuickCreateAndOpen]);

  // Filter handlers
  const toggleStatusFilter = useCallback((status: TaskStatus) => {
    const f = currentFilters.statusFilter;
    currentFilters.setStatusFilter(f.includes(status) ? f.filter(s => s !== status) : [...f, status]);
  }, [currentFilters]);

  const toggleHorizonFilter = useCallback((horizon: string) => {
    const f = currentFilters.horizonFilter;
    currentFilters.setHorizonFilter(f.includes(horizon) ? f.filter(i => i !== horizon) : [...f, horizon]);
  }, [currentFilters]);

  const toggleTagFilter = useCallback((tag: string) => {
    const f = currentFilters.tagsFilter;
    currentFilters.setTagsFilter(f.includes(tag) ? f.filter(t => t !== tag) : [...f, tag]);
  }, [currentFilters]);

  const toggleTaskTypeFilter = useCallback((type: 'all' | 'parent_only' | 'child_only') => {
    currentFilters.setTaskTypeFilter(type);
  }, [currentFilters]);

  const clearFilters = useCallback(() => {
    currentFilters.setStatusFilter([]);
    currentFilters.setHorizonFilter([]);
    currentFilters.setTagsFilter([]);
    currentFilters.setTaskTypeFilter('all');
    setSearchQuery('');
  }, [currentFilters, setSearchQuery]);

  // Loading — initial auth check only (login/register loading handled by AuthForm)
  if (!authInitialized) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-300">Loading your tasks...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <AuthForm />;
  }

  // Loading data after authentication — skeleton screen
  if (isLoadingTodos) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        {/* Skeleton header */}
        <div className="h-16 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 flex items-center justify-between">
          <div className="h-6 w-24 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
          <div className="flex gap-2">
            <div className="h-8 w-8 bg-gray-200 dark:bg-gray-700 rounded-full animate-pulse" />
            <div className="h-8 w-20 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
          </div>
        </div>
        {/* Skeleton tabs */}
        <div className="px-4 py-3 flex gap-2">
          {[80, 60, 100].map((w, i) => (
            <div key={i} className="h-8 rounded-md bg-gray-200 dark:bg-gray-700 animate-pulse" style={{ width: w }} />
          ))}
        </div>
        {/* Skeleton cards */}
        <div className="px-4 space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
              <div className="h-4 w-32 bg-gray-200 dark:bg-gray-700 rounded animate-pulse mb-3" />
              <div className="space-y-2">
                <div className="h-3 w-full bg-gray-100 dark:bg-gray-700 rounded animate-pulse" />
                <div className="h-3 w-3/4 bg-gray-100 dark:bg-gray-700 rounded animate-pulse" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // ── Mobile layout ──────────────────────────────────────────────────
  if (isMobile) {
    return (
      <>
        <Suspense
          fallback={
            <div className="h-[100dvh] bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
              <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
            </div>
          }
        >
          <MobileShell
            user={user}
            onShowSettings={() => setShowMobileSettings(true)}
            onQuickCreateAndOpen={() => handleQuickCreateAndOpen()}
          />
        </Suspense>

        <AppModals
          showTodoForm={showTodoForm}
          selectedTodo={selectedTodo}
          todoFormRef={todoFormRef}
          allTags={allTags}
          allTodos={allTodos}
          addingChildToParentId={addingChildToParentId}
          initialDueDate={initialDueDate}
          initialTodoDate={initialTodoDate}
          initialActiveInWeek={initialActiveInWeek}
          initialActiveInMonth={initialActiveInMonth}
          selectedTeamId={selectedTeamId}
          currentUser={user || undefined}
          teamMembers={teamMembers}
          isPersonalMode={isPersonalMode}
          isCreatingTodo={isCreatingTodo}
          isUpdatingTodo={isUpdatingTodo}
          onCreateTodo={handleCreateTodoAndClose}
          onUpdateTodo={handleUpdateTodo}
          onAutoSaveTodo={handleAutoSaveTodo}
          onCloseTodoModal={handleCloseTodoModal}
          onCancelTodoModal={handleCancelTodoModal}
          onDeleteTodo={handleDeleteTodo}
          onCloneTodo={handleCloneTodo}
          onSubtaskClick={handleSubtaskClick}
          onParentClick={handleParentClick}
          showTeamModal={showTeamModal}
          editingTeam={editingTeam}
          showJoinTeamModal={showJoinTeamModal}
          onCloseTeamModal={() => setShowTeamModal(false)}
          onCloseJoinTeamModal={() => setShowJoinTeamModal(false)}
          showPomodoroTimer={showPomodoroTimer}
          currentPomodoroTask={currentPomodoroTask}
          onClosePomodoroTimer={() => setShowPomodoroTimer(false)}
          setCurrentPomodoroTask={setCurrentPomodoroTask}
          showAIAssistant={showAIAssistant}
          onCloseAIAssistant={() => setShowAIAssistant(false)}
          scheduleTodo={scheduleTodo}
          editEvent={editEvent}
          onCloseEventModal={() => setScheduleTodo(null)}
          onCloseEditEvent={() => setEditEvent(null)}
          onScheduleTodoClick={(todo: Todo) => {
            setScheduleTodo(null);
            setSelectedTodo(todo);
          }}
        />

        <ProcessInboxModal
          isOpen={showProcessInbox}
          onClose={() => setShowProcessInbox(false)}
          allTodos={allTodos}
          onUpdateTodo={(id, data) => updateTodo({ id, data: data as TodoCreate })}
          onDeleteTodo={handleDeleteTodo}
          onTodoClick={(todo) => {
            setModalStack({ previousModal: 'process-inbox' });
            setShowProcessInbox(false);
            setSelectedTodo(todo);
          }}
        />

        {aiConversationId && (
          <AIChatModal
            isOpen={true}
            onClose={() => setAIConversationId(null)}
            initialConversationId={aiConversationId}
          />
        )}

        <UserSettingsModal
          isOpen={showMobileSettings}
          onClose={() => setShowMobileSettings(false)}
          selectedTeamId={selectedTeamId}
        />

        {notification && (
          <Notification type={notification.type} message={notification.message} actionText={notification.actionText} onAction={notification.onAction} onClose={hideNotification} />
        )}
      </>
    );
  }

  // ── Desktop layout ────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800">
      <AppHeader
        user={user}
        selectedTeamId={selectedTeamId}
        onTeamSelect={handleTeamSelect}
        onCreateTeam={handleCreateTeam}
        onJoinTeam={handleJoinTeam}
        onTeamSettings={handleTeamSettings}
        onShowAIAssistant={() => setShowAIAssistant(true)}
        onShowProcessInbox={() => setShowProcessInbox(true)}
        inboxCount={inboxCount}
        onShowTodoForm={() => {
          handleQuickCreateAndOpen();
        }}
      />

      <GoogleCalendarReauthBanner />

      <div className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-12 py-6">
        {/* Hide tab bar when focus session is active/paused */}
        {!focusLocked && (
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            {viewMode === 'windows' ? (
              <WindowTabs />
            ) : (
              <ViewTabs activeView={activeView} onViewChange={setActiveView} />
            )}
            {classicViewUnlocked && (
              <div className="flex items-center gap-2 ml-4">
                <button
                  onClick={() => setViewMode(viewMode === 'classic' ? 'windows' : 'classic')}
                  className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                    viewMode === 'windows'
                      ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
                      : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
                  }`}
                  title={viewMode === 'windows' ? 'Switch to classic views' : 'Switch to windows mode'}
                >
                  {viewMode === 'windows' ? 'Windows' : 'Classic'}
                </button>
              </div>
            )}
          </div>
        </div>
        )}

        {viewMode === 'windows' ? (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm min-h-[400px] sm:min-h-[600px]">
            <WindowContainer />
          </div>
        ) : (
          <>
            {activeView === 'list' && (
              <div className="mb-6">
                <SearchFilterBar
                  searchQuery={searchQuery}
                  onSearchChange={setSearchQuery}
                  showFilters={showFilters}
                  onToggleFilters={() => setShowFilters(!showFilters)}
                  onStatusToggle={toggleStatusFilter}
                  statusFilter={currentFilters.statusFilter}
                  onHorizonToggle={toggleHorizonFilter}
                  horizonFilter={currentFilters.horizonFilter}
                  onTagToggle={toggleTagFilter}
                  tagsFilter={currentFilters.tagsFilter}
                  taskTypeFilter={currentFilters.taskTypeFilter}
                  onTaskTypeChange={toggleTaskTypeFilter}
                  allTags={allTags}
                  onClearFilters={clearFilters}
                />
              </div>
            )}

            <AppViews
              activeView={activeView}
              todos={todos}
              allTodos={allTodos}
              allTags={allTags}
              selectedTeamId={selectedTeamId}
              teamMembers={teamMembers}
              isPersonalMode={isPersonalMode}
              currentUser={user || undefined}
              searchQuery={searchQuery}
              showFilters={showFilters}
              currentFilters={currentFilters}
              onTodoClick={setSelectedTodo}
              onTodoClickWithContext={handleTodoClickWithContext}
              onTodoStatusChange={handleStatusChange}
              onTodoDateChange={handleTodoDateChange}
              onAddChild={handleAddChild}
              onMakeChild={handleMakeChild}
              onStartPomodoro={handleStartPomodoro}
              onDeleteTodo={handleDeleteTodo}
              onUpdateTodo={handleUpdateTodo}
              onCancelTodoModal={handleCancelTodoModal}
              onCloneTodo={handleCloneTodo}
              onSearchChange={setSearchQuery}
              onToggleFilters={() => setShowFilters(!showFilters)}
              onStatusToggle={toggleStatusFilter}
              onHorizonToggle={toggleHorizonFilter}
              onTagToggle={toggleTagFilter}
              onTaskTypeChange={toggleTaskTypeFilter}
              onClearFilters={clearFilters}
              onCalendarCreateTodo={handleCalendarCreateTodo}
              onAddTodo={() => {
                handleQuickCreateAndOpen();
              }}
              onAddTodayTask={() => {
                handleQuickCreateAndOpen();
              }}
              onAddWeekTask={() => {
                handleQuickCreateAndOpen();
              }}
              onStartWeeklyReview={() => {}}
              onCreateTodo={async (todo: TodoCreate) => {
                return new Promise((resolve, reject) => {
                  createTodo(todo, { onSuccess: (newTodo) => resolve(newTodo), onError: (error) => reject(error) });
                });
              }}
              onUpdateTodoAsync={async (todoId: string, updates: Partial<TodoCreate>) => {
                return new Promise((resolve, reject) => {
                  updateTodo({ id: todoId, data: updates as TodoCreate }, { onSuccess: () => resolve(), onError: (error) => reject(error) });
                });
              }}
              updateTodo={updateTodo}
              onStartMonthlyReview={() => {}}
              onStartYearlyReview={() => {}}
              showNotification={showNotification}
            />
          </>
        )}
      </div>

      <AppModals
        showTodoForm={showTodoForm}
        selectedTodo={selectedTodo}
        todoFormRef={todoFormRef}
        allTags={allTags}
        allTodos={allTodos}
        addingChildToParentId={addingChildToParentId}
        initialDueDate={initialDueDate}
        initialTodoDate={initialTodoDate}
        initialActiveInWeek={initialActiveInWeek}
        initialActiveInMonth={initialActiveInMonth}
        selectedTeamId={selectedTeamId}
        currentUser={user || undefined}
        teamMembers={teamMembers}
        isPersonalMode={isPersonalMode}
        isCreatingTodo={isCreatingTodo}
        isUpdatingTodo={isUpdatingTodo}
        onCreateTodo={handleCreateTodoAndClose}
        onUpdateTodo={handleUpdateTodo}
        onAutoSaveTodo={handleAutoSaveTodo}
        onCloseTodoModal={handleCloseTodoModal}
        onCancelTodoModal={handleCancelTodoModal}
        onDeleteTodo={handleDeleteTodo}
        onCloneTodo={handleCloneTodo}
        onSubtaskClick={handleSubtaskClick}
        onParentClick={handleParentClick}
        showTeamModal={showTeamModal}
        editingTeam={editingTeam}
        showJoinTeamModal={showJoinTeamModal}
        onCloseTeamModal={() => setShowTeamModal(false)}
        onCloseJoinTeamModal={() => setShowJoinTeamModal(false)}
        showPomodoroTimer={showPomodoroTimer}
        currentPomodoroTask={currentPomodoroTask}
        onClosePomodoroTimer={() => setShowPomodoroTimer(false)}
        setCurrentPomodoroTask={setCurrentPomodoroTask}
        showAIAssistant={showAIAssistant}
        onCloseAIAssistant={() => setShowAIAssistant(false)}
        scheduleTodo={scheduleTodo}
        editEvent={editEvent}
        onCloseEventModal={() => setScheduleTodo(null)}
        onCloseEditEvent={() => setEditEvent(null)}
        onScheduleTodoClick={(todo: Todo) => {
          setScheduleTodo(null);
          setSelectedTodo(todo);
        }}
      />

      {/* Process Inbox modal */}
      <ProcessInboxModal
        isOpen={showProcessInbox}
        onClose={() => setShowProcessInbox(false)}
        allTodos={allTodos}
        onUpdateTodo={(id, data) => updateTodo({ id, data: data as TodoCreate })}
        onDeleteTodo={handleDeleteTodo}
        onTodoClick={(todo) => {
          setModalStack({ previousModal: 'process-inbox' });
          setShowProcessInbox(false);
          setSelectedTodo(todo);
        }}
      />

      {/* AI Conversation modal (opened from activity feed "via AI" badge) */}
      {aiConversationId && (
        <AIChatModal
          isOpen={true}
          onClose={() => setAIConversationId(null)}
          initialConversationId={aiConversationId}
        />
      )}

      {notification && (
        <Notification type={notification.type} message={notification.message} actionText={notification.actionText} onAction={notification.onAction} onClose={hideNotification} />
      )}
    </div>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <LocalAgentPairing />
          <WindowsProvider>
            <TodoApp />
          </WindowsProvider>
        </AuthProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}
